import { useEffect, useRef, useState } from "react";

import type { Locale, PriceQuote, ProductCatalogItem } from "@/catalog";
import ShareOpportunity from "@/components/ShareOpportunity";
import {
  CheckoutPriceChangedError,
  checkoutReturnOutcome,
  checkoutStateAfterPageRestore,
  listenForCheckoutPageRestore,
  requestCheckoutUrlAtQuote,
  type CheckoutUiState,
} from "@/checkout-client";
import { copy } from "@/copy";
import { companyPath, copyToClipboard, formatMoney } from "@/format";
import { recordProductEventWithoutBlocking } from "@/product-events-client";
import {
  opportunityCurrencyOptions,
  type OpportunityCurrency,
} from "@/pricing";
import { isUnlockReceiptData, type UnlockReceiptData } from "@/unlock-receipt";

import {
  isOpportunityAnalysisPayload,
  publicAnalysis,
  type OpportunityAnalysisPayload,
} from "./analysis";
import { Briefing, CompanyMeta, DecisionStrip } from "./Briefing";

const PRICE_POLL_MS = 60_000;
const PRICE_RETRY_MS = 5_000;

export default function DetailPage({
  item,
  locale,
  currency,
  onNavigate,
}: {
  item: ProductCatalogItem;
  locale: Locale;
  currency: OpportunityCurrency | null;
  onNavigate: (path: string) => void;
}) {
  const t = copy[locale];
  const opportunity = item.opportunity;
  const isDraft = opportunity?.status === "draft";
  const isSaleable = opportunity?.saleable === true;
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [priceState, setPriceState] = useState<"loading" | "ready" | "error">("loading");
  const [unlockedPrompt, setUnlockedPrompt] = useState<string | null>(null);
  const [unlockedAnalysis, setUnlockedAnalysis] = useState<OpportunityAnalysisPayload | null>(null);
  const [unlockReceipt, setUnlockReceipt] = useState<UnlockReceiptData | null>(null);
  const [claimState, setClaimState] = useState<"idle" | "verifying" | "error" | "done">("idle");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [checkoutState, setCheckoutState] = useState<CheckoutUiState>("idle");
  const checkoutInFlightRef = useRef(false);

  useEffect(() => listenForCheckoutPageRestore(() => {
    if (checkoutReturnOutcome(window.location.search) === "success") {
      window.location.reload();
      return;
    }
    checkoutInFlightRef.current = false;
    setCheckoutState((current) => checkoutStateAfterPageRestore(current));
  }), []);

  useEffect(() => {
    if (!opportunity || isDraft || !isSaleable || opportunity.freeSample) return;
    const controller = new AbortController();
    let refreshTimer: number | null = null;
    let inFlight = false;
    const stopPolling = () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = null;
    };
    const schedule = (delayMs: number) => {
      stopPolling();
      if (controller.signal.aborted || document.visibilityState === "hidden") return;
      refreshTimer = window.setTimeout(() => void loadPrice(), delayMs);
    };
    const loadPrice = async (): Promise<void> => {
      if (inFlight || controller.signal.aborted || document.visibilityState === "hidden") return;
      inFlight = true;
      let nextDelay = PRICE_RETRY_MS;
      try {
        const response = await fetch(`/api/price?opportunity=${encodeURIComponent(item.slug)}`, { signal: controller.signal });
        if (!response.ok) throw new Error("price_unavailable");
        setQuote((await response.json()) as PriceQuote);
        setPriceState("ready");
        nextDelay = PRICE_POLL_MS;
      } catch (error: unknown) {
        if (!(error instanceof DOMException && error.name === "AbortError")) setPriceState("error");
      } finally {
        inFlight = false;
        schedule(nextDelay);
      }
    };
    const startPolling = () => {
      stopPolling();
      if (document.visibilityState !== "hidden") void loadPrice();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") stopPolling();
      else startPolling();
    };
    startPolling();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      controller.abort();
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isDraft, isSaleable, item.slug, opportunity]);

  useEffect(() => {
    if (!opportunity || isDraft || !isSaleable) return;
    const controller = new AbortController();
    let cancelled = false;
    const fetchReceipt = async (): Promise<void> => {
      const response = await fetch(`/api/access/library?locale=${locale}`, {
        signal: controller.signal,
      });
      if (!response.ok) return;
      const body = (await response.json()) as {
        opportunities?: Array<{ slug?: string; receipt?: unknown }>;
      };
      const candidate = body.opportunities?.find(({ slug }) => slug === item.slug)?.receipt;
      if (!cancelled && isUnlockReceiptData(candidate)) setUnlockReceipt(candidate);
    };
    const fetchPrompt = async (): Promise<boolean> => {
      const response = await fetch(
        `/api/opportunities/${encodeURIComponent(item.slug)}/prompt?locale=${locale}`,
        { signal: controller.signal },
      );
      if (!response.ok) return false;
      const body = (await response.json()) as { prompt?: string; analysis?: unknown };
      if (!body.prompt || !isOpportunityAnalysisPayload(body.analysis)) return false;
      if (!cancelled) {
        setUnlockedPrompt(body.prompt);
        setUnlockedAnalysis(body.analysis);
      }
      await fetchReceipt().catch(() => undefined);
      return true;
    };
    const restore = async () => {
      setUnlockedPrompt(null);
      setUnlockedAnalysis(null);
      setUnlockReceipt(null);
      const params = new URLSearchParams(window.location.search);
      const checkoutOutcome = checkoutReturnOutcome(window.location.search);
      const checkoutSessionId = params.get("session_id");
      if (checkoutOutcome === "cancelled") {
        setCheckoutState("cancelled");
        const clean = new URL(window.location.href);
        clean.searchParams.delete("checkout");
        clean.searchParams.delete("session_id");
        window.history.replaceState({}, "", `${clean.pathname}${clean.search}${clean.hash}`);
      }
      if (checkoutOutcome !== "success" || !checkoutSessionId) {
        await fetchPrompt().catch(() => false);
        return;
      }
      setClaimState("verifying");
      for (const delayMs of [0, 750, 1_500, 3_000, 5_000]) {
        if (delayMs) await new Promise((resolve) => window.setTimeout(resolve, delayMs));
        if (cancelled) return;
        const response = await fetch("/api/access/claim", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ checkoutSessionId }),
          signal: controller.signal,
        });
        if (response.ok) {
          const claimBody = (await response.json().catch(() => null)) as {
            receipt?: unknown;
          } | null;
          if (!cancelled && isUnlockReceiptData(claimBody?.receipt)) {
            setUnlockReceipt(claimBody.receipt);
          }
          const loaded = await fetchPrompt();
          if (!cancelled && loaded) {
            setClaimState("done");
            const clean = new URL(window.location.href);
            clean.searchParams.delete("checkout");
            clean.searchParams.delete("session_id");
            window.history.replaceState({}, "", `${clean.pathname}${clean.search}${clean.hash}`);
          }
          return;
        }
        if (response.status !== 409) break;
      }
      if (!cancelled) setClaimState("error");
    };
    void restore().catch((error: unknown) => {
      if (!cancelled && !(error instanceof DOMException && error.name === "AbortError")) {
        setClaimState("error");
      }
    });
    return () => { cancelled = true; controller.abort(); };
  }, [isDraft, isSaleable, item.slug, locale, opportunity]);

  const fullPrompt = isDraft || !isSaleable
    ? undefined
    : opportunity?.freePrompt?.[locale] ?? unlockedPrompt ?? undefined;
  const analysis = unlockedAnalysis ?? (opportunity?.freeSample ? publicAnalysis(opportunity, locale) : null);
  const isLocked = Boolean(opportunity && isSaleable && !isDraft && !opportunity.freeSample && (!fullPrompt || !analysis));
  const currentPrice = quote && currency
    ? quote.currencyOptions[currency].amountCents
    : null;
  const nextBasePrice = Math.min(
    (quote?.priceCents ?? opportunity?.pricing.startCents ?? 100)
      + (opportunity?.pricing.stepCents ?? 400),
    quote?.capCents ?? opportunity?.pricing.capCents ?? Number.MAX_SAFE_INTEGER,
  );
  const nextPrice = currency
    ? opportunityCurrencyOptions(nextBasePrice)[currency].amountCents
    : null;
  const priceLabel = currentPrice !== null && currency
    ? formatMoney(currentPrice, locale, currency)
    : undefined;

  const copyPrompt = async () => {
    if (!fullPrompt) return;
    try {
      await copyToClipboard(fullPrompt);
      setCopyState("copied");
      recordProductEventWithoutBlocking("prompt_copied", item.slug, locale, "detail");
    } catch {
      setCopyState("error");
    }
    window.setTimeout(() => setCopyState("idle"), 2200);
  };
  const checkout = async () => {
    if (!quote || !currency || priceState !== "ready" || checkoutInFlightRef.current) return;
    checkoutInFlightRef.current = true;
    setCheckoutState("opening");
    try {
      if (!Number.isSafeInteger(quote.slotIndex)) throw new Error("price_unavailable");
      window.location.assign(await requestCheckoutUrlAtQuote(locale, item.slug, {
        priceCents: quote.priceCents,
        slotIndex: Number(quote.slotIndex),
      }));
    } catch (error) {
      checkoutInFlightRef.current = false;
      if (error instanceof CheckoutPriceChangedError) {
        setQuote(error.quote);
        setPriceState("ready");
        setCheckoutState("idle");
        return;
      }
      setCheckoutState("error");
    }
  };

  const checkoutBusy = checkoutState === "opening" || claimState === "verifying" || priceState !== "ready" || !quote || !currency;
  const checkoutLabel = claimState === "verifying"
    ? t.verifyingPayment
    : checkoutState === "opening"
      ? t.checkoutOpening
      : quote && currentPrice !== null && currency
        ? `${t.unlockAnalysis} · ${formatMoney(currentPrice, locale, currency)}`
        : t.unlockAnalysis;

  const purchaseCard = isLocked ? (
    <section className="purchase-panel">
      <span>{t.priceNow}</span>
      <div className="detail-price-row">
        <strong>{quote && currentPrice !== null && currency ? formatMoney(currentPrice, locale, currency) : <span className="price-skeleton" aria-hidden="true" />}</strong>
        <small>
          {priceState === "error" ? t.priceRefreshing : quote && nextPrice !== null && currency ? <>{quote.confirmedPurchases} {t.buyers}<br />{t.nextPrice}: {formatMoney(nextPrice, locale, currency)}</> : t.priceLoading}
        </small>
      </div>
      <button
        type="button"
        className="button button-accent"
        disabled={checkoutBusy}
        aria-busy={checkoutState === "opening" || claimState === "verifying" || priceState !== "ready"}
        onClick={() => void checkout()}
      >
        {checkoutLabel}
      </button>
      {checkoutState === "error" && <p className="checkout-error" role="alert">{t.checkoutError}</p>}
      {claimState === "error" && <p className="checkout-error" role="alert">{t.paymentVerificationError}</p>}
      {checkoutState === "cancelled" && <p className="checkout-notice" role="status">{t.checkoutCancelled}</p>}
      {checkoutState === "returned" && <p className="checkout-notice" role="status">{t.checkoutReturned}</p>}
      <p>{t.priceMechanic}</p>
      <button
        type="button"
        className="detail-recovery-link"
        onClick={() => onNavigate(`/${locale}/access?return=${encodeURIComponent(companyPath(locale, item.slug))}`)}
      >
        {t.recoverAccess} →
      </button>
      <div className="detail-purchase-includes">
        <span>{t.previewInside}</span>
        <ul>{t.insideItems.map((value) => <li key={value}>{value}</li>)}</ul>
      </div>
    </section>
  ) : null;

  return (
    <main id="main-content" className={`detail-main${isLocked ? " is-locked" : ""}`}>
      <button className="back-button" type="button" onClick={() => onNavigate(`/${locale}/#explore`)}>← {t.back}</button>
      <DecisionStrip
        item={item}
        locale={locale}
        t={t}
        analysis={analysis}
        isLocked={isLocked}
        priceLabel={priceLabel}
        copyState={copyState}
        onCopyPrompt={fullPrompt ? () => void copyPrompt() : undefined}
        onJumpPrompt={fullPrompt ? () => {
          const el = document.getElementById("prompt");
          if (el instanceof HTMLDetailsElement) el.open = true;
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        } : undefined}
      />

      {isLocked && opportunity ? (
        <div className="locked-layout">
          <div className="locked-copy">
            {item.sourceOneLiner ? <p className="detail-one-liner">{item.sourceOneLiner}</p> : null}
            <CompanyMeta item={item} locale={locale} t={t} />
            <p className="editorial-note">{t.curationBoundary}</p>
            <div className="locked-teaser">
              {opportunity.outcomeBrief?.[locale] && <p>{opportunity.outcomeBrief[locale]}</p>}
              <h2>{opportunity.title[locale]}</h2>
              {opportunity.teaser?.[locale] && <p>{opportunity.teaser[locale]}</p>}
            </div>
            <ShareOpportunity locale={locale} opportunitySlug={item.slug} companyName={item.name} placement="preview" className="detail-share" />
          </div>
          {purchaseCard}
        </div>
      ) : opportunity ? (
        isDraft || !isSaleable ? (
          <div className="briefing-document is-solo">
            {item.sourceOneLiner ? <p className="detail-one-liner">{item.sourceOneLiner}</p> : null}
            <CompanyMeta item={item} locale={locale} t={t} />
            <p className="editorial-note is-draft">{t.draftBoundary}</p>
            <section className="load-failure" role="alert">
              <h2>{t.noCurationTitle}</h2>
              <p>{t.noCurationBody}</p>
            </section>
          </div>
        ) : analysis ? (
          <Briefing
            item={item}
            locale={locale}
            t={t}
            analysis={analysis}
            opportunity={opportunity}
            fullPrompt={fullPrompt}
            copyState={copyState}
            onCopyPrompt={() => void copyPrompt()}
            unlockReceipt={unlockReceipt}
            isDraft={isDraft}
          />
        ) : null
      ) : (
        <section className="load-failure" role="alert">
          <h2>{t.noCurationTitle}</h2>
          <p>{t.noCurationBody}</p>
        </section>
      )}

      {isLocked && (
        <div className="locked-dock">
          <div>
            <small>{t.priceNow}</small>
            <strong>{quote && currentPrice !== null && currency ? formatMoney(currentPrice, locale, currency) : <span className="price-skeleton" aria-hidden="true" />}</strong>
          </div>
          <button
            type="button"
            className="button button-accent"
            disabled={checkoutBusy}
            aria-busy={checkoutState === "opening" || claimState === "verifying" || priceState !== "ready"}
            onClick={() => void checkout()}
          >
            {checkoutLabel}
          </button>
        </div>
      )}
      <p className="legal-boundary">{t.legal}</p>
    </main>
  );
}
